import {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import Api from '../../../core/services/api/api';
import useIsMounted from '../use-is-mounted';
import AggregatedVariantsNexusSearch from '../../../core/services/nexus-search/aggregated-variants';

/**
 * Loads industry variance-hierarchy properties for Aggregated Variants Nexus Search.
 *
 * @param {string|number|null|undefined} industryId
 * @param {boolean} [enabled]
 */
const useIndustryVarianceProperties = (industryId, enabled = true) => {
    const isMounted = useIsMounted();
    const [varianceProperties, setVarianceProperties] = useState([]);
    const [loading, setLoading] = useState(() => Boolean(industryId && enabled));

    /**
     * Fetches property extra configs and maps variance hierarchy.
     */
    const fetchConfigs = useCallback(async (cancelToken) => {
        if (!industryId || !enabled) {
            setVarianceProperties([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        const response = await Api.getIndustryPropertyExtraConfigs(industryId, cancelToken);
        if (!isMounted()) {
            return;
        }
        if (!response?.aborted) {
            setLoading(false);
        }
        if (response?.resultFlag) {
            setVarianceProperties(
                AggregatedVariantsNexusSearch.resolveVariancePropertiesFromExtraConfig(response.data),
            );
        } else {
            setVarianceProperties([]);
        }
    }, [enabled, industryId, isMounted]);

    useEffect(() => {
        if (!industryId || !enabled) {
            setVarianceProperties([]);
            return;
        }
        const cancelTokenSource = axios.CancelToken.source();
        void fetchConfigs(cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [fetchConfigs, industryId, enabled]);

    return {varianceProperties, loading};
};

export default useIndustryVarianceProperties;
